/**
 * Client half of the universal attachment-content resolver (2026-08-02).
 *
 * Signed GCS attachment URLs work for navigation (open in tab / download) but not
 * for browser fetch() — the bucket serves no CORS headers — so the previewer's
 * byte-needing renderers (PDF, DOCX, sheets, text, HTML) degraded in production.
 * When direct fetch fails, PreviewContent falls back here: an authenticated POST
 * to prest0nResolveAttachmentContent, which reads the object server-side and
 * returns it inline. Mirrors resolve3d.ts (same endpoint family, same auth).
 */
import { getAuth } from 'firebase/auth';
import { CF_BASE_URL } from './resolve3d';
import type { SerializedAttachment } from '../GlobalChatSystem/types';

export interface ResolvedAttachmentContent {
  bytes: ArrayBuffer;
  contentType: string;
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out.buffer;
}

/**
 * Message context forwarded to the resolver so a record with NO usable
 * locator (the race shapes: optimistic bubble before upload upgrade, doc
 * echoes, mirrors that dropped fields) can still be found server-side by
 * reconstructing the storage paths the platform writes (2026-08-09).
 */
export interface AttachmentResolveContext {
  messageId?: string;
  /**
   * Shared thread the message lives in. Lets a PARTICIPANT read a teammate's
   * uploaded photo server-side (owner report 2026-09-07) — the server checks
   * live participation and that the object was posted in this very message,
   * so a signed URL expiring after 7 days no longer loses the picture.
   */
  threadId?: string;
  sessionId?: string;
  turnId?: string;
}

/** True when the attachment carries ANY source the server resolver can use —
 *  a direct locator, the cloud-ingest gcsPath, or reconstructable context. */
export function canResolveServerSide(
  serialized?: SerializedAttachment | null,
  context?: AttachmentResolveContext | null,
): boolean {
  if (!serialized) return false;
  if (serialized.storagePath || serialized.storageUrl || serialized.gcsPath) return true;
  return Boolean(
    serialized.name && context && (context.messageId || (context.sessionId && context.turnId)),
  );
}

export async function resolveAttachmentContent(
  serialized: SerializedAttachment,
  context?: AttachmentResolveContext | null,
): Promise<ResolvedAttachmentContent> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('auth-required');
  const token = await user.getIdToken();
  const resp = await fetch(`${CF_BASE_URL}/prest0nResolveAttachmentContent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      storagePath: serialized.storagePath || undefined,
      gcsPath: serialized.gcsPath || undefined,
      storageUrl: serialized.storagePath ? undefined : serialized.storageUrl || undefined,
      name: serialized.name || undefined,
      ...(context && (context.messageId || (context.sessionId && context.turnId))
        ? {
          context: {
            messageId: context.messageId || undefined,
            threadId: context.threadId || undefined,
            sessionId: context.sessionId || undefined,
            turnId: context.turnId || undefined,
            name: serialized.name || undefined,
          },
        }
        : {}),
    }),
  });
  if (!resp.ok) throw new Error(resp.status === 404 ? 'not-found' : `resolver-${resp.status}`);
  const data = (await resp.json()) as { base64?: string; contentType?: string };
  if (!data || typeof data.base64 !== 'string') throw new Error('resolver-malformed');
  return {
    bytes: base64ToArrayBuffer(data.base64),
    contentType: typeof data.contentType === 'string' ? data.contentType : '',
  };
}
